import { GiRhinocerosHorn, GiSnail } from "react-icons/gi";
import {
  LuCalendar,
  LuCheckCircle,
  LuCoffee,
  LuCopy,
  LuDices,
  LuLink,
  LuLock,
  LuTimer,
  LuX,
  LuZap,
} from "react-icons/lu";
import React from "react";
import "./App.css";
import CryptoJS from "crypto-js";
import { storeCypher } from "./api";
import { generatePassword } from "./password";
import { buildSecretPayload } from "./payload";

const APP_BASE_URL =
  process.env.REACT_APP_APP_BASE_URL || "http://localhost:3000";

const MAX_RECENT_LINKS = 5;

type RecentLink = {
  uuid: string;
  url: string;
  expiration: string;
  createdAt: Date;
};

function formatLinkTime(date: Date) {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

function expirationLabel(value: string) {
  const labels: Record<string, string> = {
    "1m": "1 minute",
    "1h": "1 hour",
    "1d": "1 day",
    "1w": "1 week",
    "1mo": "1 month",
  };
  return labels[value] ?? value;
}

function App() {
  const maxLength = 256;
  const [text, setText] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [selectedTime, setSelectedTime] = React.useState("1d");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);
  const pendingRef = React.useRef(false);
  const [recentLinks, setRecentLinks] = React.useState<RecentLink[]>([]);
  const [readyLink, setReadyLink] = React.useState<RecentLink | null>(null);
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [copiedUuid, setCopiedUuid] = React.useState<string | null>(null);
  const copyTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  function deriveKeyFromPassword(password: string): string {
    const salt = CryptoJS.lib.WordArray.random(16).toString();
    return CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: 1000,
    }).toString();
  }

  const encryptAES = (
    plainText: string | CryptoJS.lib.WordArray,
    secretKey: string | CryptoJS.lib.WordArray
  ) => {
    return CryptoJS.AES.encrypt(plainText, secretKey).toString();
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(event.target.value);
  };

  const handleEncrypt = async () => {
    if (!text || pendingRef.current) {
      return;
    }

    pendingRef.current = true;
    setErrorMessage(null);
    setPending(true);

    try {
      const key = deriveKeyFromPassword(generatePassword());
      const encryptedText = encryptAES(buildSecretPayload(text, message), key);
      const expiration = selectedTime;
      const response = await storeCypher(encryptedText, expiration);

      if (!response?.success || !response?.data?.uuid) {
        setErrorMessage(response?.message || "Failed to store secret");
        return;
      }

      const linkUrl = `${APP_BASE_URL}/retrieve-cypher/${response.data.uuid}/${key}`;
      const createdLink = {
        uuid: response.data.uuid,
        url: linkUrl,
        expiration,
        createdAt: new Date(),
      };
      setRecentLinks((current) =>
        [createdLink, ...current].slice(0, MAX_RECENT_LINKS)
      );
      setReadyLink(createdLink);
      setLinkCopied(false);
      setText("");
      setMessage("");
    } catch {
      setErrorMessage("Failed to store secret");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const handleCopyLink = async (link: RecentLink) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedUuid(link.uuid);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedUuid((current) => (current === link.uuid ? null : current));
      }, 2000);
    } catch {
      setErrorMessage("Failed to copy link");
    }
  };

  const handleRemoveLink = (uuid: string) => {
    setRecentLinks((current) => current.filter((link) => link.uuid !== uuid));
    setCopiedUuid((current) => (current === uuid ? null : current));
  };

  const handleClearHistory = () => {
    setRecentLinks([]);
    setCopiedUuid(null);
  };

  const handleClear = () => {
    setText("");
  };

  const handleGenerate = () => {
    if (
      text &&
      !window.confirm("Replace the current secret with a generated password?")
    ) {
      return;
    }
    setText(generatePassword());
  };

  const handleCopyReadyLink = async () => {
    if (!readyLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(readyLink.url);
      setLinkCopied(true);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch {
      setErrorMessage("Failed to copy link");
    }
  };

  const handleHome = () => {
    setText("");
    setMessage("");
    setSelectedTime("1d");
    setErrorMessage(null);
    setReadyLink(null);
    setLinkCopied(false);
  };

  const charactersLeft = maxLength - text.length;
  const canEncrypt = text.length > 0 && !pending;

  return (
    <div className="App">
      <main>
        <div className="brand scale-up-reveal">
          <h1 className="back-to-future-title zoom-on-click" onClick={handleHome}>
            PassLink
          </h1>
          {readyLink ? (
            <div className="link-ready-copy">
              <p className="link-ready-title">
                <LuCheckCircle />
                Your encrypted link is ready
              </p>
              <p className="link-ready-subtitle">
                Share this link with your recipient
              </p>
            </div>
          ) : (
            <p className="tagline">Share secrets. Simply</p>
          )}
        </div>
        {errorMessage && (
          <p className="slide-in-left" style={{ color: "red" }}>
            Error: {errorMessage}
          </p>
        )}

        {!readyLink && (
        <>
        <div className="secret-fields">
          <div className="input-wrapper scale-up-reveal">
            <textarea
              aria-label="Secret"
              className={text.includes("\n") ? undefined : "single-line"}
              placeholder="Enter or paste your secret"
              maxLength={maxLength}
              rows={4}
              value={text}
              onChange={handleChange}
              autoComplete="off"
              spellCheck={false}
            ></textarea>
            <span className="char-count">{charactersLeft} characters left</span>
            {text && (
              <button
                type="button"
                className="input-clear-btn"
                onClick={handleClear}
                title="Clear text"
                aria-label="Clear text"
              >
                <LuX />
              </button>
            )}
            <button
              type="button"
              className="input-dice-btn"
              onClick={handleGenerate}
              title="Generate password"
              aria-label="Generate password"
            >
              <LuDices />
            </button>
          </div>
          <div className="message-field">
            <input
              type="text"
              aria-label="Message"
              placeholder="Message"
              maxLength={32}
              value={message}
              autoComplete="off"
              onChange={(event) => setMessage(event.target.value.slice(0, 32))}
            />
          </div>
        </div>

        <div className="radio-group slide-in-right">
          {[
            {
              label: "1 minute",
              value: "1m",
              icon: <LuZap />,
            },
            {
              label: "1 hour",
              value: "1h",
              icon: <LuCoffee />,
            },
            { label: "1 day", value: "1d", icon: <GiRhinocerosHorn className="time-joker" /> },
            { label: "1 week", value: "1w", icon: <GiSnail className="time-joker" /> },
            {
              label: "1 month",
              value: "1mo",
              icon: <LuCalendar />,
            },
          ].map((option) => (
            <label
              className="expiration"
              key={option.value}
              title={option.label}
            >
              <input
                className="expiration"
                type="radio"
                name="expiration"
                value={option.value}
                checked={selectedTime === option.value}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
              {option.icon}
              <span>{option.value}</span>
            </label>
          ))}
        </div>

        <div className="button-group">
          <button
            className={`encrypt-btn slide-in-left ${canEncrypt ? "" : "disabled"}`}
            onClick={handleEncrypt}
            disabled={!canEncrypt}
          >
            <LuLock style={{ marginRight: "0.5em" }} />
            Encrypt & create link
          </button>
        </div>
        </>
        )}

        {readyLink && (
          <div className="link-ready scale-up-reveal">
            <div className="share-link">
              <div className="share-link-url">
                <LuLink />
                <span>{readyLink.url}</span>
              </div>
              <button type="button" className="encrypt-btn" onClick={handleCopyReadyLink}>
                {linkCopied ? <LuCheckCircle /> : <LuCopy />}
                {linkCopied ? "Copied!" : "Copy link"}
              </button>
            </div>
            <p className="expires-after">
              <LuTimer />
              Expires after {expirationLabel(readyLink.expiration)}
            </p>
            <button type="button" className="create-another-btn" onClick={handleHome}>
              + Create a new secret
            </button>
          </div>
        )}

        {!readyLink && recentLinks.length > 0 && (
          <section className="recent-links" aria-label="Recent links">
            <div className="recent-links-header">
              <h2>Recent links</h2>
              <button
                type="button"
                className="recent-text-btn"
                onClick={handleClearHistory}
              >
                Clear all
              </button>
            </div>
            <ul>
              {recentLinks.map((link) => (
                <li key={link.uuid}>
                  <span className="recent-time">{formatLinkTime(link.createdAt)}</span>
                  <span className="recent-uuid">{link.uuid.slice(0, 8)}</span>
                  <span className="recent-expiration">
                    {expirationLabel(link.expiration)}
                  </span>
                  <button
                    type="button"
                    className="recent-text-btn"
                    onClick={() => handleCopyLink(link)}
                  >
                    {copiedUuid === link.uuid ? (
                      <>
                        <LuCheckCircle />
                        Copied!
                      </>
                    ) : (
                      <>
                        <LuCopy />
                        Copy
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="recent-remove-btn"
                    onClick={() => handleRemoveLink(link.uuid)}
                    title="Remove"
                    aria-label={`Remove link ${link.uuid.slice(0, 8)}`}
                  >
                    <LuX />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
